import type { DrivetrainState } from '../drivetrain/VirtualDrivetrain';
import { synthesizeNoise } from './ProceduralVoice';

/**
 * EnvironmentProcessor — non-engine layers. Phase 1 ships a speed-driven wind
 * bed; the node is the hook for tyre noise, tunnel reverb, track ambience.
 */
export class EnvironmentProcessor {
  readonly output: GainNode;
  private readonly windGain: GainNode;
  private windSource: AudioBufferSourceNode | null = null;
  private readonly maxGain: number;

  private readonly ambGain: GainNode;
  private ambSource: AudioBufferSourceNode | null = null;
  private readonly ambMax: number;

  private readonly pitGain: GainNode;
  private pitLife: AudioBuffer[] = [];
  private nextPitAt = 0;
  // pit-row bed: the long "cars passing" recordings play back to back, in full, while the box is alive
  private bed: AudioBuffer[] = [];
  private bedIdx = 0;
  private bedSrc: AudioBufferSourceNode | null = null;
  private bedNextAt = 0;

  private debris: AudioBuffer | null = null;
  private readonly debrisGain: GainNode;
  private nextDebrisAt = 0;

  // THE SHOWROOM (Marc, 2026-09-02) — what the road cars do instead of a pit box: an Italian bed comes
  // up now and then and a storyteller talks over it about the car, quietly. Parked, engine off, only.
  private readonly music: AudioBuffer | null;
  private readonly story: AudioBuffer | null;
  private readonly showGain: GainNode;
  private musicSrc: AudioBufferSourceNode | null = null;
  private storySrc: AudioBufferSourceNode | null = null;
  /** each spot's own faders: cutting one fades THESE, never the shared bus (a bus left at 0 is a
   *  showroom that never comes back — it did exactly that the first time) */
  private spotGains: GainNode[] = [];
  private spotAt = 0;          // when the next spot may begin
  private spotEnds = 0;        // …and when the one that is running is over
  private lastStoryAt = -1e9;  // the voice is not on a loop: once, then rarely
  // BACKGROUND, AND MEANT IT (Marc: "tiene que ser musica de fondo, que tú tengas el volumen a tope y
  // se escuche bajito", then "BAJA MÁS EL VOLUMEN!!!!!!!!"). The first cut had the bed 10 dB under the
  // engine's own idle and the voice level with it, which is a podcast; 12 dB off that was still not
  // background, and 22 dB still was not ("el volumen más bajo aún!!!"). Thirty now — a thirtieth of
  // the amplitude it started at. The bed sits about 41 dB under the idle the driver set the volume for
  // and the voice about 31, which on a car stereo at driving volume is a quiet conversation in a
  // parked car. The 15 dB of voice-over spacing between them has never moved, so he stays intelligible
  // however far both go. Nothing else scales this: these three numbers are the whole control.
  // (The master compressor cannot be putting it back either — threshold -10 dBFS, and Web Audio's
  // compressor has no makeup gain, so at these levels it is not in the circuit at all.)
  private static readonly MUSIC_GAIN = 0.015;
  private static readonly MUSIC_DUCK = 0.0085;
  private static readonly STORY_GAIN = 0.038;
  private static readonly STORY_EVERY_S = 300;

  constructor(
    private readonly ctx: AudioContext,
    windBuffer: AudioBuffer | null = null,
    ambienceBuffer: AudioBuffer | null = null,
    pitLife: AudioBuffer[] = [],
    debrisBuffer: AudioBuffer | null = null,
    showroom: { music: AudioBuffer | null; story: AudioBuffer | null } | null = null,
  ) {
    this.output = ctx.createGain();
    // long clips (> 20 s) are the continuous bed (fast bys 1–4); short ones stay random one-shots (wrench, jack)
    this.bed = pitLife.filter((b) => b.duration > 20);
    this.pitLife = pitLife.filter((b) => b.duration <= 20);
    this.bedIdx = Math.floor(Math.random() * Math.max(1, this.bed.length));
    this.pitGain = ctx.createGain();
    this.pitGain.gain.value = 0;
    this.pitGain.connect(this.output);
    this.nextPitAt = ctx.currentTime + 4 + Math.random() * 6;
    // pit / garage ambience: real loop if the car ships one, else a low room tone
    this.ambGain = ctx.createGain();
    this.ambGain.gain.value = 0;
    const amb = ctx.createBufferSource();
    amb.buffer = ambienceBuffer ?? synthesizeNoise(ctx, 3);
    amb.loop = true;
    if (ambienceBuffer) {
      amb.connect(this.ambGain).connect(this.output);
    } else {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 180;
      lp.Q.value = 0.5;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 40;
      amb.connect(hp).connect(lp).connect(this.ambGain).connect(this.output);
    }
    this.ambSource = amb;
    // no real pit recording = SILENCE while parked. The synthetic room-tone fallback read as 'ruido
    // de viento' on the sim prototypes (Marc, v369): a parked car with the engine off makes no sound.
    this.ambMax = ambienceBuffer ? 0.5 : 0;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = windBuffer ? 2500 : 700;
    lp.Q.value = 0.4;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    const src = ctx.createBufferSource();
    src.buffer = windBuffer ?? synthesizeNoise(ctx, 2);
    src.loop = true;
    src.connect(lp).connect(this.windGain).connect(this.output);
    this.windSource = src;
    // no real wind recording = NO WIND. The synthesised fallback used to breathe at up to 0.4 on any
    // car without one, which is a noise the engine never made (Marc: "quítale todo tipo de sonido de
    // aire y de viento, quiero que suene solo las RPM").
    this.maxGain = windBuffer ? 0.9 : 0;
    // stones/debris: random short grains from the bodywork clip, fired now and then at speed (Marc's 'piedrecitas')
    this.debris = debrisBuffer;
    this.debrisGain = ctx.createGain();
    this.debrisGain.gain.value = 1;
    this.debrisGain.connect(this.output);
    this.nextDebrisAt = ctx.currentTime + 8;
    // the showroom, on its own bus so a spot can be cut off cleanly when the engine fires
    this.music = showroom?.music ?? null;
    this.story = showroom?.story ?? null;
    this.showGain = ctx.createGain();
    this.showGain.gain.value = 1;
    this.showGain.connect(this.output);
    // NO WAITING (Marc: "la musica deberia aparecer inmediatamente segun cargue"). This processor is
    // built when the atmosphere finishes downloading, which is already several seconds into the
    // driver sitting there in silence; adding a random pause on top of that just made it look broken.
    // The bed's own three-second fade is the entrance. The storyteller still follows a few seconds
    // later — the driver may press ENG START at once, and a narrator nobody ever hears is pointless.
    this.spotAt = ctx.currentTime;
  }

  start(): void {
    try {
      this.windSource?.start();
    } catch {
      /* already started */
    }
    try {
      this.ambSource?.start();
    } catch {
      /* already started */
    }
  }

  update(s: DrivetrainState): void {
    // wind you can actually HEAR next to a full-blast engine (Marc): starts breathing at ~25 km/h and
    // grows steadily — the old (v-35)²·0.6/220 curve gave 0.05 of 0.8 at 100 km/h, inaudible
    const v = Math.max(0, s.speedKmh - 25) / 160;
    const g = Math.min(this.maxGain, Math.pow(v, 1.3));   // Marc round 2: 'apenas escucho viento' — ~2.5× louder at road speeds
    this.windGain.gain.setTargetAtTime(g, this.ctx.currentTime, 0.25);
    // 'piedrecitas': every so often at speed a short grain of the bodywork clip — random offset, random
    // length, louder and more frequent the faster you go; silent under 55 km/h and while braking hard
    if (this.debris && s.speedKmh > 55 && s.brake < 0.5 && this.ctx.currentTime >= this.nextDebrisAt) {
      const now2 = this.ctx.currentTime;
      const spd = Math.min(1, (s.speedKmh - 55) / 130);
      const dur = 0.25 + Math.random() * 0.6;
      const offset = Math.random() * Math.max(0.1, this.debris.duration - dur - 0.1);
      const src = this.ctx.createBufferSource();
      src.buffer = this.debris;
      const grain = this.ctx.createGain();
      const peak = (0.2 + 0.35 * spd) * (0.7 + Math.random() * 0.3);   // Marc round 2: 'apenas escucho piedrecitas' — ~2.5× louder
      grain.gain.setValueAtTime(0, now2);
      grain.gain.linearRampToValueAtTime(peak, now2 + 0.04);
      grain.gain.setValueAtTime(peak, now2 + dur - 0.08);
      grain.gain.linearRampToValueAtTime(0, now2 + dur);
      src.connect(grain).connect(this.debrisGain);
      src.start(now2, offset, dur + 0.05);
      src.onended = () => { try { src.disconnect(); grain.disconnect(); } catch { /* noop */ } };
      this.nextDebrisAt = now2 + dur + 2.5 + Math.random() * (14 - 10 * spd);   // ~3-17 s apart, tighter at speed
    }
    // ambience: full when parked with the engine off, half with it idling, gone once rolling
    const parked = Math.max(0, 1 - s.speedKmh / 12);
    // the box goes quiet the moment the engine runs (Marc: "cuando arranques el motor se calla todo")
    const a = this.ambMax * parked * (s.engine === 'off' ? 1 : 0);
    this.ambGain.gain.setTargetAtTime(a, this.ctx.currentTime, s.engine === 'off' ? 0.6 : 0.25);
    // pit life: random one-shots (cars passing, air wrench, air jack) while parked — quieter with the engine running
    const pg = parked * (s.engine === 'off' ? 0.9 : 0);
    this.pitGain.gain.setTargetAtTime(pg, this.ctx.currentTime, s.engine === 'off' ? 0.5 : 0.2);
    const now = this.ctx.currentTime;
    // the bed: always on while the box is audible (engine off) — whole files, chained with a 2 s crossfade
    if (this.bed.length && s.engine === 'off' && parked > 0.5 && now >= this.bedNextAt) {
      const buf = this.bed[this.bedIdx % this.bed.length];
      this.bedIdx++;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.9, now + 2);
      g.gain.setValueAtTime(0.9, now + buf.duration - 2);
      g.gain.linearRampToValueAtTime(0, now + buf.duration);
      src.connect(g).connect(this.pitGain);
      src.start(now);
      src.onended = () => { try { src.disconnect(); g.disconnect(); } catch { /* noop */ } };
      this.bedSrc = src;
      this.bedNextAt = now + buf.duration - 2;
    }
    // THE SHOWROOM. Same gate as the pit box - standing still, engine off - but nothing about it is a
    // loop: a spot starts, the bed comes up from a random place in the mix, the storyteller talks over
    // it once, and then it all goes away again for a minute or two (Marc: "de manera aleatoria").
    if (this.music) {
      if (parked > 0.5 && s.engine === 'off') {
        if (now >= this.spotAt && now >= this.spotEnds) this.beginSpot(now);
      } else if (this.musicSrc || this.storySrc) {
        // ENG START, or the car moved: the showroom is over mid-sentence, which is right — nobody
        // listens to a voice-over with a V12 running. The next spot starts fresh rather than resuming.
        this.endSpot(now);
        this.spotAt = now + 8 + Math.random() * 10;
      }
    }
    if (this.pitLife.length && parked > 0.5 && s.engine === 'off' && now >= this.nextPitAt) {
      const buf = this.pitLife[Math.floor(Math.random() * this.pitLife.length)];
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = 0.96 + Math.random() * 0.08;
      const g = this.ctx.createGain();
      g.gain.value = 0.6 + Math.random() * 0.4;
      src.connect(g).connect(this.pitGain);
      src.start();
      this.nextPitAt = now + buf.duration * 0.6 + 6 + Math.random() * 14;
    }
  }

  /**
   * One showroom spot: the bed, and the story over it if it has not been told for a while.
   *
   * The whole shape is scheduled up front on the AudioParam timeline rather than driven from update():
   * the frame loop stops when the tab is hidden, and a duck that never came back up would leave the
   * voice talking under a bed at full level.
   */
  private beginSpot(now: number): void {
    const music = this.music;
    if (!music) return;
    const M = EnvironmentProcessor.MUSIC_GAIN, D = EnvironmentProcessor.MUSIC_DUCK;
    const tell = !!this.story && now - this.lastStoryAt > EnvironmentProcessor.STORY_EVERY_S;
    // the first read comes soon after the driver sits down; later beds run on their own for a while
    const lead = tell ? (this.lastStoryAt < 0 ? 5 + Math.random() * 4 : 8 + Math.random() * 8) : 0;
    const talk = tell ? (this.story as AudioBuffer).duration : 0;
    const len = tell ? lead + talk + 6 + Math.random() * 6 : 45 + Math.random() * 35;
    const offset = Math.random() * Math.max(0, music.duration - len - 1);

    const src = this.ctx.createBufferSource();
    src.buffer = music;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(M, now + 3);
    if (tell) {
      g.gain.setValueAtTime(M, now + lead - 1.5);
      g.gain.linearRampToValueAtTime(D, now + lead - 0.2);          // out of the way before he starts
      g.gain.setValueAtTime(D, now + lead + talk);
      g.gain.linearRampToValueAtTime(M, now + lead + talk + 2.5);   // …and back once he is done
    }
    g.gain.setValueAtTime(M, now + len - 4);
    g.gain.linearRampToValueAtTime(0, now + len);
    src.connect(g).connect(this.showGain);
    src.start(now, offset, len + 0.2);
    src.onended = () => { try { src.disconnect(); g.disconnect(); } catch { /* noop */ } };
    this.musicSrc = src;
    this.spotGains = [g];

    if (tell && this.story) {
      const vo = this.ctx.createBufferSource();
      vo.buffer = this.story;
      const vg = this.ctx.createGain();
      vg.gain.value = 0;   // an AudioParam holds 1 until its first scheduled event: silent from birth
      vg.gain.setValueAtTime(0, now + lead - 0.15);
      vg.gain.linearRampToValueAtTime(EnvironmentProcessor.STORY_GAIN, now + lead + 0.15);
      vo.connect(vg).connect(this.showGain);
      vo.start(now + lead);
      vo.onended = () => { try { vo.disconnect(); vg.disconnect(); } catch { /* noop */ } };
      this.storySrc = vo;
      this.spotGains.push(vg);
      this.lastStoryAt = now;
    }
    this.spotEnds = now + len;
    this.spotAt = this.spotEnds + 40 + Math.random() * 70;
  }

  /**
   * ENG START, called BEFORE the starter clip: the showroom gets out of the way and then leaves a
   * hole. Marc: "cuando aprietas el start up, la voz se calla y LUEGO se enciende… deja unas décimas
   * en silencio para que se aprecie el arranque." Returns the seconds the ignition should wait, so
   * the crank lands in silence instead of on top of a fading voice (0 when nothing was playing).
   */
  hush(): number {
    if (!this.musicSrc && !this.storySrc) return 0;
    const now = this.ctx.currentTime;
    this.endSpot(now, 0.25);
    this.spotAt = now + 30;   // and it does not come creeping back the moment the engine is switched off
    return 0.25 + 0.3;        // the fade, and then the beat of silence the start is worth
  }

  /** cut the spot short: fade its own faders, then stop, so ENG START does not chop a word in half */
  private endSpot(now: number, fadeS = 0.4): void {
    for (const g of this.spotGains) {
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(0, now + fadeS);
    }
    for (const src of [this.musicSrc, this.storySrc]) {
      try { src?.stop(now + fadeS + 0.05); } catch { /* noop */ }
    }
    this.spotGains = [];
    this.musicSrc = null;
    this.storySrc = null;
    this.spotEnds = 0;
  }

  stop(): void {
    for (const src of [this.windSource, this.ambSource, this.bedSrc, this.musicSrc, this.storySrc]) {
      try {
        src?.stop();
      } catch {
        /* noop */
      }
    }
    this.windSource = null;
    this.ambSource = null;
    this.bedSrc = null;
    this.musicSrc = null;
    this.storySrc = null;
  }
}
